import { ChangeDetectionStrategy, Component, inject, output, signal } from '@angular/core';
import { ActionIconDef, Grid as InavistaGrid, GridActionIconClickEvent, GridConfig, GridHyperlinkClickEvent, GridRow } from '@inavista/components/grid';
import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { ImpersonationService } from 'projects/service/src/app/shared/services/impersonation.service';
import { environment } from 'projects/service/src/environments/environment';

// User menu → "Impersonate…" (Dev / Test / Stage, RIMS admins): pick the user to work as. Rows: RIMS API
// api/grid/impersonation-users (every enabled RIMS Service user but you), loaded once and searched in the browser.
const COLUMNS = ['name', 'userName', 'email', 'employeeId', 'role', 'serviceDepartment'];
const IMPERSONATE_ICON = '<circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />';

@Component({
  selector: 'app-impersonation-picker',
  templateUrl: './impersonation-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InavistaGrid],
})
export class ImpersonationPickerComponent {
  private readonly impersonation = inject(ImpersonationService);

  readonly closed = output<void>();
  readonly isExpanded = signal(false);
  readonly busy = signal('');
  readonly error = signal('');

  readonly actionIcons: ActionIconDef[] = [
    { key: 'impersonate', label: 'Impersonate this user', icon: IMPERSONATE_ICON, colorFn: () => '#000000de' },
  ];
  readonly gridConfig: GridConfig = {
    apiBaseUrl: `${environment.apiUrl}grid/impersonation-users`,
    keyColumn: 'id',
    clientSide: true,
    rowHeight: 26,
    loadingIndicator: 'banner',
    stretchColumns: true,
    enableSearch: true,
    enableContextMenuFilter: true,
    enableFitColumns: true,
    enableResetLayout: true,
    defaultColumnOrder: COLUMNS,
    hiddenByDefaultColumns: ['id'],
    columnLabels: {
      name: 'Name', userName: 'Username', email: 'Email', employeeId: 'Employee ID', role: 'Role', serviceDepartment: 'Department',
    },
    columnAlign: Object.fromEntries(COLUMNS.map(c => [c, { header: { horizontal: 'left' as const } }])),
    defaultSort: { column: 'name', direction: 'asc' },
    hyperlinkColumns: ['name'],
    // "View by role" (Martin, 2026-11-02): the toolbar's Group by; the choice persists with the rest of the layout.
    groupableColumns: ['role', 'serviceDepartment'],
    emptyStateMessage: 'No other RIMS Service user to impersonate.',
    noResultsMessage: 'No user matches. Search on name, username, email or employee ID, or clear the filters.',
    persistenceKey: 'rims.service.impersonation-users',
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
    persistenceVersion: 1,
  };

  onActionIconClick(event: GridActionIconClickEvent) {
    if (event.iconKey === 'impersonate') this.pick(event.row);
  }

  onHyperlinkClick(event: GridHyperlinkClickEvent) {
    this.pick(event.row);
  }

  // On success the page reloads as the picked user; on failure the reason stays on screen.
  private async pick(row: GridRow) {
    if (this.busy()) return;
    const userName = String(row['userName'] ?? '');
    this.error.set('');
    this.busy.set(`Switching to ${row['name'] || userName}…`);
    const failure = await this.impersonation.start(userName);
    if (failure) {
      this.busy.set('');
      this.error.set(failure);
    }
  }

  expandCollapse() {
    this.isExpanded.update(v => !v);
  }

  close() {
    if (!this.busy()) this.closed.emit();
  }
}
